import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Fire, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const type = params.get("type"); // 'premium' or default
  const [status, setStatus] = useState("polling");
  const [tx, setTx] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { navigate("/"); return; }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          toast.success(type === "premium" ? "Premium attivato!" : "Pagamento confermato! Evento in evidenza attivato.");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch {}
      if (attempts < 10) {
        setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      {status === "polling" && (
        <>
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">Elaborazione</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-4">Confermando il pagamento...</h1>
          <p className="text-neutral-400">Un momento, stiamo attivando il tuo boost.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle size={80} weight="fill" className="text-primary mx-auto mb-6" />
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3 flex items-center justify-center gap-2">
            <Fire size={12} weight="fill" /> {type === "premium" ? "Premium attivato" : "Boost attivato"}
          </div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-4" data-testid="payment-success-title">
            {type === "premium" ? "Sei ufficialmente Premium" : "Il tuo evento è ora in evidenza"}
          </h1>
          <p className="text-neutral-400 mb-8">
            {type === "premium" ? "Grazie! Il tuo abbonamento è attivo per 30 giorni." : "Grazie! Il tuo evento apparirà in cima ai risultati e nella homepage."}
          </p>
          <Button asChild className="bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm h-12 px-8" data-testid="payment-success-continue">
            <Link to={type === "premium" ? "/profile" : `/events/${tx?.event_id || ""}`}>
              {type === "premium" ? "Vai al profilo" : "Vedi evento"} <ArrowRight size={16} weight="bold" className="ml-2" />
            </Link>
          </Button>
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <XCircle size={80} weight="fill" className="text-destructive mx-auto mb-6" />
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-4">Pagamento non confermato</h1>
          <p className="text-neutral-400 mb-8">Se hai completato il pagamento, ricevi una conferma tra pochi minuti. Riprova se necessario.</p>
          <Button asChild variant="outline" className="border-border rounded-sm uppercase tracking-wider text-xs font-bold">
            <Link to="/events">Torna agli eventi</Link>
          </Button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const eventId = params.get("event_id");
  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <XCircle size={80} weight="fill" className="text-neutral-500 mx-auto mb-6" />
      <div className="text-xs font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">Annullato</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-4">Pagamento annullato</h1>
      <p className="text-neutral-400 mb-8">Nessun addebito effettuato. Puoi riprovare quando vuoi.</p>
      <Button asChild className="bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm h-12 px-8">
        <Link to={eventId ? `/events/${eventId}` : "/events"}>Torna all'evento</Link>
      </Button>
    </div>
  );
}
